import React, { useEffect, useRef, useState } from "react";
import {
  Calendar,
  XCircle,
  CreditCard,
  FileText,
  ShieldCheck,
  Globe,
  Key,
  Brush,
} from "lucide-react";

const sections = [
  {
    id: "durations",
    icon: <Calendar size={18} className="text-blue-500" />,
    title: "Booking Durations & Modifications",
    content: (
      <div className="space-y-3 text-sm text-gray-600">
        <p><strong>Available Durations:</strong> Subscriptions can be booked for fixed terms of 7 days, 15 days, 30 days, 60 days and 90 days.</p>
        <div>
          <p className="font-semibold mb-1">Extensions</p>
          <p>Extensions must be requested under the original Booking ID.</p>
          <ul className="list-disc ml-5 mt-1 space-y-1">
            <li><strong>First Extension:</strong> The booking may be extended for a maximum of 48 hours (2 days), subject to vehicle availability.</li>
            <li><strong>Second Extension:</strong> The user must select the same subscription plan/package that was chosen at the time of the original booking. For example, if the original booking was made under a 7-day package, the second extension must also be for 7 days.</li>
          </ul>
          <p className="mt-1">All extensions are subject to vehicle availability.</p>
          <p>Extension pricing may vary if the extension period falls during a peak season, holiday period, or other applicable high-demand period.</p>
        </div>
        <div>
          <p className="font-semibold mb-1">Rescheduling</p>
          <p>Bookings may be rescheduled for an equivalent duration based on selected subscription plan. Requests must be submitted at least <strong>24 hours prior</strong> to the scheduled booking start date and time.</p>
          <p>In case a vehicle rescheduling is requested by the user, the rescheduling will be permitted subject to vehicle availability and the availability of the requested vehicle model.</p>
          <ul className="list-disc ml-5 mt-1 space-y-1">
            <li>Pricing may vary depending on the vehicle model selected for the rescheduling.</li>
            <li>If the rescheduling date falls during a peak season, holiday period, or other high-demand period, the applicable rental pricing may also vary.</li>
            <li>Any additional charges or price differences resulting from the vehicle model or applicable seasonal pricing will be communicated to the user before confirmation.</li>
          </ul>
        </div>
        <div>
          <p className="font-semibold mb-1">Swapping</p>
          <p>In case a vehicle swap is requested by the user, the swap will be permitted subject to vehicle availability and the availability of the requested vehicle model.</p>
          <ul className="list-disc ml-5 mt-1 space-y-1">
            <li>Pricing may vary depending on the vehicle model selected for the swap.</li>
            <li>If the swap date falls during a peak season, holiday period, or other high-demand period, the applicable rental pricing may also vary.</li>
            <li>Any additional charges or price differences resulting from the vehicle model or applicable seasonal pricing will be communicated to the user before confirmation.</li>
            <li>The vehicle swap will be confirmed only after vehicle availability and applicable pricing are verified.</li>
          </ul>
        </div>
        <div>
          <p className="font-semibold mb-1">Upgrades</p>
          <p>If the user wishes to upgrade the vehicle model, the upgrade will be permitted subject to the availability of the requested vehicle model.</p>
          <ul className="list-disc ml-5 mt-1 space-y-1">
            <li>Pricing may vary based on the vehicle model selected for the upgrade.</li>
            <li>If the upgrade date falls during a peak season, holiday period, or other high-demand period, the applicable pricing may vary based on the rates available on the date of the upgrade request.</li>
            <li>Any applicable price difference or additional charges will be calculated based on the selected vehicle model and prevailing pricing at the time of the request.</li>
            <li>The upgrade will be confirmed only after vehicle availability and applicable pricing are verified.</li>
          </ul>
        </div>
      </div>
    ),
  },
  {
    id: "cancellation",
    icon: <XCircle size={18} className="text-red-500" />,
    title: "Cancellation & Refund Policy",
    content: (
      <div className="space-y-3 text-sm text-gray-600">
        <p>Cancellations are subject to the following terms:</p>
        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="font-semibold text-yellow-800">Cancellation Before 24 Hours</p>
          <p className="text-yellow-700">If the user requests cancellation more than 24 hours before the scheduled trip start date and time, a 50% cancellation fee will be deducted. The remaining 50% will be refunded to the customer's original payment source within 7 to 10 bank working days.</p>
        </div>
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="font-semibold text-red-800">Cancellation Within 24 Hours</p>
          <p className="text-red-700">If the user requests cancellation within 24 hours of the scheduled trip start date and time, the booking will be considered a 100% cancellation. Only the security deposit will be refunded to the customer.</p>
        </div>
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="font-semibold text-red-800">No-Show</p>
          <p className="text-red-700">If the customer does not report for the booking at the scheduled trip start date and time without cancelling the booking, it will be treated as a No-Show and a 100% cancellation fee will apply. Only the security deposit will be refunded.</p>
        </div>
      </div>
    ),
  },
  {
    id: "financials",
    icon: <CreditCard size={18} className="text-green-500" />,
    title: "Financials, FASTAG & Fuel Policy",
    content: (
      <div className="space-y-3 text-sm text-gray-600">
        <p><strong>Mandatory Fees:</strong> The initial checkout total comprises the Base Fare, applicable Taxes, Security Deposit, and a mandatory minimum FASTAG recharge of 1000 INR.</p>
        <p><strong>FASTAG Usage:</strong> FASTAG expenses exceeding the initial recharge amount will be deducted directly from the security deposit.</p>
        <p><strong>Over Speeding:</strong> Approved Speed limit is 80-110 km/hr, exceeding this will incur penalty of 500 INR for each violation.</p>
        <p><strong>Fuel Policy:</strong> Vehicles must be returned with the exact same fuel level as provided at the start of the subscription. For Low fuel we do charge 800 INR per point. For 20% excess fuel than the start fuel %, customer has to submit proper printed bill with Vehicle registration number, Date and time matching the subscription period.</p>
        <p><strong>Late Hour & Extra KM Charges:</strong> Extra hours and extra kilometres will be calculated based on the official booking start and end timestamps. If customer informed 6 hours prior to his drop off time, then 250/- per hour will be charged. If customer informed within 6 hours to his drop off time, then 400/- will be charged.</p>
        <p><strong>KM Packages:</strong> Customers are allowed to drive overall free kms allocated based on selected km package, exceeding the free kms will incur extra km charges based on vehicle model.</p>
      </div>
    ),
  },
  {
    id: "kyc",
    icon: <FileText size={18} className="text-purple-500" />,
    title: "KYC & Driver Eligibility Requirements",
    content: (
      <div className="space-y-3 text-sm text-gray-600">
        <p>The following documents are strictly mandatory for both primary and secondary drivers prior to vehicle handover. <strong>Digi-Locker documents are not accepted.</strong></p>
        <ul className="list-disc ml-5 space-y-1">
          <li><strong>Identification:</strong> Valid Driving License (LMV) issued at least 1 year prior to the booking start date, and Aadhaar for both drivers.</li>
          <li>
            <strong>Address Proof:</strong> Local or non-local address verification documents, alongside a valid alternate contact number.
            <ul className="list-disc ml-5 mt-1 space-y-1">
              <li>Local address proof – Valid Rental agreement or Latest Gas bill or Latest WIFI bill or Valid Passport or Company ID or Valid PG bill</li>
              <li>Non – Local address proof – Valid Travel tickets, Proof of stay – Hotel Bookings.</li>
              <li>For International Customers: Valid International Driving license and driving permit, valid passport copy, Travel tickets, Hotel bookings are mandatory.</li>
            </ul>
          </li>
          <li><strong>Age Requirement:</strong> All drivers must be at least 21 years of age.</li>
        </ul>
        <p>For vehicle handover process – submission of one original document is mandatory DL or Aadhar or Pan-Card or Passport or Company Id – which will be returned back post trip completion.</p>
        <p className="text-red-600 font-medium">Note: Verification Failure due to invalid documents leads to cancelation with 100% deduction. Only deposit will be refunded back to source account.</p>
      </div>
    ),
  },
  {
    id: "compliance",
    icon: <Globe size={18} className="text-teal-500" />,
    title: "Compliance, Damage & Interstate Travel",
    content: (
      <div className="space-y-3 text-sm text-gray-600">
        <p><strong>Interstate Permits:</strong> Customers are solely responsible for purchasing and maintaining all required interstate permit taxes, E-Pass which is mandatory to cross state border and check posts. Parking charges need to be paid by customers.</p>
        <p><strong>Accident Policy:</strong> In occurrence of vehicle damage, customer shall be sole responsible for all such loss in regardless of cause, or who, or what caused it.</p>
        <ul className="list-disc ml-5 space-y-1">
          <li><strong>Under ₹30,000 damage:</strong> Customer liable to pay damage fee equivalent to repair plus Downtime losses (rent for equivalent repair days).</li>
          <li><strong>Over ₹30,000 damage:</strong> Comprehensive insurance claimed; customer pays estimation difference plus Downtime losses.</li>
          <li><strong>Total Loss:</strong> Comprehensive insurance claimed; customer pays estimation difference, parking charges, plus 3 months downtime losses.</li>
        </ul>
        <p className="font-semibold">Negligence & Prohibited Situations (Irrespective of Insurance):</p>
        <ul className="list-disc ml-5 space-y-1">
          <li>Consequential damage (driving while abnormal)</li>
          <li>Beach/off-road drive (Rs 8000/- penalty + damages)</li>
          <li>Under influence of alcohol/narcotics (Full cost)</li>
          <li>Different driver than booking person</li>
          <li>Exceeding seating capacity</li>
          <li>Failure to pay inter-state tax</li>
          <li>Rash and negligent driving</li>
          <li>Violation of traffic rules/Motor Vehicles Act</li>
        </ul>
        <p><strong>Towing Charges:</strong> User negligence/accident- Full cost should be paid by the customer.</p>
      </div>
    ),
  },
  {
    id: "cleanliness",
    icon: <Brush size={18} className="text-orange-500" />,
    title: "Cleanliness & Vehicle Maintenance",
    content: (
      <div className="space-y-2 text-sm text-gray-600">
        <p>Customers must ensure the vehicle is not soiled. Penalty/damages will be collected from customers minimum <strong>INR 1250</strong></p>
        <ul className="list-disc ml-5 space-y-1">
          <li>Chewing gum/Tobacco</li>
          <li>Burning any substance (Cigarettes/Incense)</li>
          <li>Visible fabric stains/Torn fabric</li>
          <li>Pets inside vehicle</li>
          <li>Presence of sand/excessive mud</li>
        </ul>
        <p><strong>Running Repair:</strong> Wow carz cannot assume responsibility for unforeseen electrical malfunctions. In instances where the vehicle is operated using the half-clutch method, customer will be responsible for bearing the charges for clutch plate wear/repair as prescribed by our fleet executive.</p>
        <p><strong>Key Loss:</strong> If the customer lost the key due to their negligence, the charges would be Minimum INR 15,000. Charges may increase based on vehicle models.</p>
        <p><strong>Personal Belongings:</strong> Wow carz will not hold responsible for any damage to, Loss or theft of any personal property belonging to user or co-passenger or third party regardless of fault or negligence.</p>
      </div>
    ),
  },
  {
    id: "jurisdiction",
    icon: <ShieldCheck size={18} className="text-gray-500" />,
    title: "Jurisdiction Process",
    content: (
      <p className="text-sm text-gray-600">
        Any proceedings, interim or interlocutory relief, arising from arbitration shall be brought in a court of competent jurisdiction in Bengaluru. Submission to Bengaluru Jurisdiction doesn't limit the customer right to commence proceedings in any other jurisdiction it deems appropriate.
      </p>
    ),
  },
];

const SubscriptionTermsandconditions = () => {
  const [activeId, setActiveId] = useState(sections[0].id);
  const sectionRefs = useRef({});
  const observerRef = useRef(null);
  const navRef = useRef(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: 0 },
    );

    sections.forEach(({ id }) => {
      const el = sectionRefs.current[id];
      if (el) observerRef.current.observe(el);
    });

    return () => observerRef.current?.disconnect();
  }, []);

  useEffect(() => {
    const activeEl = navRef.current?.querySelector(`[data-id="${activeId}"]`);
    activeEl?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [activeId]);

  const scrollTo = (id) => {
    sectionRefs.current[id]?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen bg-transparent pt-20 md:pt-24 text-left">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        {/* Header */}
        <div className="mb-10 text-center">
          <h1 className="text-3xl md:text-4xl font-black text-gray-900 uppercase tracking-tight">
            Subscription Terms & Conditions
          </h1>
          <p className="text-gray-500 mt-2 text-sm">
            Please read these terms carefully before proceeding with your subscription booking.
          </p>
        </div>

        <div className="flex gap-8 items-start">
          {/* Sticky side nav — desktop only */}
          <aside className="hidden lg:block w-56 flex-shrink-0 sticky top-28 self-start max-h-[calc(100vh-8rem)] overflow-y-auto pb-4 scrollbar-hide [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]" ref={navRef}>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Sections</p>
            <nav className="space-y-1 pb-28">
              {sections.map(({ id, icon, title }) => (
                <button
                  key={id}
                  data-id={id}
                  onClick={() => scrollTo(id)}
                  className={`w-full flex items-center gap-2 text-left px-3 py-2 rounded-lg text-sm transition-all ${
                    activeId === id
                      ? "bg-red-50 text-red-600 font-semibold"
                      : "text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                  }`}
                >
                  <span className="flex-shrink-0">{icon}</span>
                  <span className="leading-tight">{title}</span>
                </button>
              ))}
            </nav>
          </aside>

          {/* Content */}
          <div className="flex-1 space-y-10">
            {sections.map(({ id, icon, title, content }) => (
              <section
                key={id}
                id={id}
                ref={(el) => (sectionRefs.current[id] = el)}
                className="scroll-mt-28"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-white border border-gray-200 rounded-lg shadow-sm">
                    {icon}
                  </div>
                  <h2 className="text-base md:text-lg font-bold text-gray-800">{title}</h2>
                </div>
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm px-5 py-4 leading-relaxed">
                  {content}
                </div>
              </section>
            ))}

            <p className="text-gray-400 text-xs italic pb-28">
              Wow Carz is not responsible for loss or theft of personal property regardless of fault or negligence.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionTermsandconditions;
